import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { VisitModel } from 'src/app/models/preliminaries/visit.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PendingVisitTableModel } from 'src/app/models/tables/preliminaries/pending-visit-table.model';
import { VisitTableModel } from 'src/app/models/tables/preliminaries/visit-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VisitService {

  private readonly _httpClient = inject(HttpClient);

  blockVisit(visitId: number): Observable<VisitModel> {
    return this._httpClient.put<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.BlockVisitAsync}/${visitId}`, null);
  }

  changeState(visitModel: VisitModel): Observable<VisitModel> {
    return this._httpClient.put<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.ChangeStateAsync}`, visitModel);
  }

  createVisit(visitContract: VisitModel): Observable<VisitModel> {
    return this._httpClient.post<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.CreateAsync}`, visitContract);
  }

  deleteVisit(visitId: number): Observable<VisitModel> {
    return this._httpClient.delete<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.DeleteAsync}/${visitId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  getAllConcepsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetAllConcepsForSelectAsync}`);
  }

  getAllPendingVisitsWithPagination(clientId: number | null, page: number, limit: number, columnSort: string | null, ascendant: boolean): Observable<PagedResponseModel<PendingVisitTableModel>> {
    let params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    if (clientId !== null && clientId !== undefined) {
      params = params.set('clientId', clientId);
    }
    return this._httpClient.post<PagedResponseModel<PendingVisitTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetAllPendingVisitsWithPaginationAsync}`, null, { params }
    );
  }

  getAllVisitsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<VisitTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<VisitTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetAllVisitsWithPaginationAsync}`, filters, { params }
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForPendingVisits(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetColumnsForPendingVisitsAsync}`);
  }

  getFirstVisitByKeysAsync(branchId: number): Observable<VisitModel> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetFirstVisitByKeys}`,
      { params: params }
    );
  }

  getLastVisitByKeysAsync(branchId: number): Observable<VisitModel> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetLastVisitByKeys}`,
      { params: params }
    );
  }

  getNextVisitByKeysAsync(branchId: number, consecutive: number | null): Observable<VisitModel> {
    let params = new HttpParams().set('branchId', branchId)
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetNextVisitByKeys}`,
      { params: params }
    );
  }

  getPermission(permission: number): Observable<boolean> {
    const params = new HttpParams().set('permission', permission);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetPermissionAsync}`, { params });
  }

  getPreviusVisitByKeysAsync(branchId: number, consecutive: number | null): Observable<VisitModel> {
    let params = new HttpParams().set('branchId', branchId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetPreviusVisitByKeys}`,
      { params: params }
    );
  }

  getVisitById(visitId: number): Observable<VisitModel> {
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetByIdAsync}/${visitId}`);
  }

  getVisitByKeysAsync(branchId: number, consecutive: number): Observable<VisitModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive.toString());
    return this._httpClient.get<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.GetVisitByKeysAsync}`, { params });
  }

  updateVisit(visitContract: VisitModel): Observable<VisitModel> {
    return this._httpClient.put<VisitModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.visit.UpdateAsync}`, visitContract);
  }

}
